import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { LLCurrency } from '@/types/lead-lifetime';

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const BRL4 = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 4 });
const USD = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'USD' });
const USD4 = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 4 });

export interface Money {
    /** Valor cheio (R$ 1.234,56 ou US$ 240,65). */
    fmt: (v: number | null | undefined) => string;
    /** Valor por lead/usuário, com até 4 casas (R$ 0,0873). */
    unit: (v: number | null | undefined) => string;
    currency: LLCurrency;
    rate: number;
}

/**
 * O MS devolve tudo em BRL (já líquido). O dólar só existe na tela: divide-se pela mesma
 * cotação que o backend usou no cálculo, que vem em `meta.dolar_rate`.
 */
function build(requested: LLCurrency, rate: number): Money {
    // Sem cotação (resposta ainda sem `meta`), não dá pra converter: mostra em real.
    const currency: LLCurrency = requested === 'USD' && rate > 0 ? 'USD' : 'BRL';
    const convert = (v: number | null | undefined) => (v == null ? null : currency === 'USD' && rate > 0 ? v / rate : v);
    const full = currency === 'USD' ? USD : BRL;
    const four = currency === 'USD' ? USD4 : BRL4;
    return {
        currency,
        rate,
        fmt: (v) => {
            const value = convert(v);
            return value == null ? '—' : full.format(value);
        },
        unit: (v) => {
            const value = convert(v);
            return value == null ? '—' : four.format(value);
        },
    };
}

const MoneyContext = createContext<Money>(build('BRL', 1));

export function MoneyProvider({ currency, rate, children }: { currency: LLCurrency; rate: number; children: ReactNode }) {
    const value = useMemo(() => build(currency, rate), [currency, rate]);
    return <MoneyContext.Provider value={value}>{children}</MoneyContext.Provider>;
}

export const useMoney = () => useContext(MoneyContext);
